<template>
  <div>
    <div class="bg-white rounded-xl border border-gray-200 card-shadow">
      <!-- Header + Tabs -->
      <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h3 class="text-[15px] font-semibold text-gray-900">Schedules</h3>
          <p class="text-sm text-gray-400 mt-0.5">Automated start/stop for this channel</p>
        </div>
        <button v-if="activeTab === 'manage' && $checkPermission('schedulers.create')" @click="openForm(null)" class="btn-primary !text-xs !px-3 !py-1.5">
          <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Schedule
        </button>
      </div>
      <div class="px-6 border-b border-gray-100">
        <div class="flex gap-0">
          <button
            v-for="tab in [{ key: 'today', label: 'Today' }, { key: 'manage', label: 'Manage' }]"
            :key="tab.key"
            @click="activeTab = tab.key"
            :class="['relative px-5 py-3.5 text-sm font-medium transition-colors', activeTab === tab.key ? 'text-[var(--color-primary)]' : 'text-gray-400 hover:text-gray-600']"
          >
            {{ tab.label }}
            <span v-if="activeTab === tab.key" class="absolute bottom-0 left-0 right-0 h-[2px] bg-[var(--color-primary)] rounded-full" />
          </button>
        </div>
      </div>

    <!-- Today's Schedule tab -->
    <div v-if="activeTab === 'today'">
        <div v-if="loadingToday" class="p-4 space-y-2">
          <div v-for="i in 3" :key="'skel-' + i" class="flex items-center gap-3 px-5 py-4 rounded-lg bg-gray-50 border border-gray-100">
            <div class="w-2 h-2 rounded-full bg-gray-200 skeleton-pulse shrink-0" />
            <div class="flex-1 space-y-2">
              <div class="h-3.5 bg-gray-200 rounded skeleton-pulse" :style="{ width: (40 + i * 10) + '%' }" />
              <div class="h-3 bg-gray-100 rounded skeleton-pulse w-32" />
            </div>
          </div>
        </div>
        <div v-else-if="todaySchedules.length" class="p-4 space-y-2">
          <div class="flex items-center gap-2 px-5 py-4 rounded-lg bg-blue-50/60 border border-blue-100">
            <svg class="w-3.5 h-3.5 text-blue-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <p class="text-sm text-blue-500">Showing in <span class="font-medium">{{ localTzShort }}</span> · click times to toggle original timezone</p>
          </div>
          <div v-for="s in todaySchedules" :key="s._id"
            :class="['flex items-center gap-4 px-5 py-4 rounded-lg border transition-colors', s.is_running ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-100']"
          >
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1.5">
                <span v-if="s.is_running" class="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse shrink-0" />
                <span class="text-sm font-semibold text-gray-900 truncate">{{ s.title }}</span>
                <span v-if="s.is_running" class="badge-live">Live</span>
                <span v-if="s.status === 'paused'" class="badge-warning">paused</span>
                <span v-if="s.enable_recording && s.is_running" class="text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-50 text-red-500 inline-flex items-center gap-1"><span class="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />Recording</span>
                <span v-else-if="s.enable_recording" class="text-[10px] font-medium px-1.5 py-0.5 rounded bg-orange-50 text-orange-500">Rec Enabled</span>
              </div>
              <div class="flex items-center gap-5 text-sm text-gray-500">
                <span class="flex items-center gap-1.5 cursor-pointer select-none" @click.stop="toggleTimeDisplay(s._id)" :title="showOriginalTime[s._id] ? 'Click to show local time' : 'Click to show original time'">
                  <svg class="w-3.5 h-3.5 shrink-0" :class="s.is_running ? 'text-green-400' : 'text-gray-400'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  <template v-if="showOriginalTime[s._id]">
                    {{ formatDisplayTime(s, 'start', true) }} <span class="text-gray-300 mx-0.5">→</span> {{ formatDisplayTime(s, 'end', true) }} <span v-if="s.timezone" class="text-gray-400">({{ getTimezoneLabel(s.timezone) }})</span>
                  </template>
                  <template v-else>
                    {{ formatDisplayTime(s, 'start', false) }} <span class="text-gray-300 mx-0.5">→</span> {{ formatDisplayTime(s, 'end', false) }} <span class="text-gray-400">({{ localTzShort }})</span>
                  </template>
                </span>
                <span v-if="s.frequency && s.frequency.length" class="text-gray-400">
                  {{ s.frequency.map(d => d.slice(0, 3)).join(', ') }}
                </span>
              </div>
              <div v-if="s.from_date" class="flex items-center gap-1.5 text-sm text-gray-400 mt-0.5">
                <svg class="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                {{ $filters.formatDate(s.from_date) }} <span v-if="s.to_date" class="text-gray-300">→</span> {{ s.to_date ? $filters.formatDate(s.to_date) : 'No end date' }}
              </div>
            </div>
            <!-- Edit -->
            <button v-if="$checkPermission('schedulers.update')" @click="openForm(s)" :class="['p-2 rounded-lg transition-colors shrink-0', s.is_running ? 'text-green-600 hover:text-[var(--color-primary)] hover:bg-white' : 'text-gray-400 hover:text-[var(--color-primary)] hover:bg-[var(--color-primary-light)]']" title="Edit">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            </button>
          </div>
        </div>
        <div v-else class="px-6 py-16 text-center">
          <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          </div>
          <p class="text-base font-medium text-gray-900">No schedules for today</p>
          <p class="text-sm text-gray-500 mt-1">Schedules assigned to today will appear here</p>
        </div>
    </div>

    <!-- Manage Schedules tab -->
    <div v-if="activeTab === 'manage'">
        <div v-if="loadingAll" class="p-4 space-y-2">
          <div v-for="i in 4" :key="'skel-' + i" class="flex items-center gap-3 px-5 py-4 rounded-lg bg-gray-50 border border-gray-100">
            <div class="flex-1 space-y-2">
              <div class="h-3.5 bg-gray-200 rounded skeleton-pulse" :style="{ width: (40 + i * 10) + '%' }" />
              <div class="h-3 bg-gray-100 rounded skeleton-pulse w-40" />
            </div>
            <div class="h-8 bg-gray-100 rounded-lg skeleton-pulse w-24" />
          </div>
        </div>
        <div v-else-if="schedules.length" class="p-4 space-y-2">
          <div class="flex items-center gap-2 px-5 py-4 rounded-lg bg-blue-50/60 border border-blue-100">
            <svg class="w-3.5 h-3.5 text-blue-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <p class="text-sm text-blue-500">Showing in <span class="font-medium">{{ localTzShort }}</span> · click times to toggle original timezone</p>
          </div>
          <div v-for="s in schedules" :key="s._id"
            :class="['flex items-center gap-4 px-5 py-4 rounded-lg border transition-colors', s.is_running ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-100']"
          >
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1.5">
                <span v-if="s.is_running" class="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse shrink-0" />
                <span class="text-sm font-semibold text-gray-900 truncate">{{ s.title }}</span>
                <span v-if="s.is_running" class="badge-live">Live</span>
                <span v-if="s.status === 'paused'" class="badge-warning">paused</span>
                <span v-if="s.enable_recording && s.is_running" class="text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-50 text-red-500 inline-flex items-center gap-1"><span class="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />Recording</span>
                <span v-else-if="s.enable_recording" class="text-[10px] font-medium px-1.5 py-0.5 rounded bg-orange-50 text-orange-500">Rec Enabled</span>
                <span v-if="s.recurrency === 'recurrent'" class="text-[10px] font-medium px-1.5 py-0.5 rounded bg-violet-50 text-violet-500">Recurrent</span>
                <span v-else class="text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-500">One Time</span>
              </div>
              <div class="flex items-center gap-5 text-sm text-gray-500">
                <span class="flex items-center gap-1.5 cursor-pointer select-none" @click.stop="toggleTimeDisplay(s._id)" :title="showOriginalTime[s._id] ? 'Click to show local time' : 'Click to show original time'">
                  <svg class="w-3.5 h-3.5 shrink-0" :class="s.is_running ? 'text-green-400' : 'text-gray-400'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  <template v-if="showOriginalTime[s._id]">
                    {{ formatDisplayTime(s, 'start', true) }} <span class="text-gray-300 mx-0.5">→</span> {{ formatDisplayTime(s, 'end', true) }} <span v-if="s.timezone" class="text-gray-400">({{ getTimezoneLabel(s.timezone) }})</span>
                  </template>
                  <template v-else>
                    {{ formatDisplayTime(s, 'start', false) }} <span class="text-gray-300 mx-0.5">→</span> {{ formatDisplayTime(s, 'end', false) }} <span class="text-gray-400">({{ localTzShort }})</span>
                  </template>
                </span>
                <span v-if="s.frequency && s.frequency.length" class="text-gray-400">
                  {{ s.frequency.map(d => d.slice(0, 3)).join(', ') }}
                </span>
              </div>
              <div v-if="s.from_date" class="flex items-center gap-1.5 text-sm text-gray-400 mt-0.5">
                <svg class="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                {{ $filters.formatDate(s.from_date) }} <span v-if="s.to_date" class="text-gray-300">→</span> {{ s.to_date ? $filters.formatDate(s.to_date) : 'No end date' }}
              </div>
              <!-- Tags, Category & Attributes -->
              <div v-if="(s.tags && s.tags.length) || (s.categories && s.categories.length) || (s.attributes && s.attributes.length)" class="flex flex-wrap items-center gap-2 mt-1.5">
                <span v-if="s.tags && s.tags.length" class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium bg-[var(--color-primary-light)] text-[var(--color-primary)]">
                  <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                  {{ s.tags.length }}
                </span>
                <span v-if="s.categories && s.categories.length" class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium bg-violet-50 text-violet-500">
                  <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
                  {{ s.categories.length }}
                </span>
                <span v-for="attr in (s.attributes || []).filter(a => a.value)" :key="attr._id" class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
                  {{ attr.code }}: {{ attr.value }}
                </span>
              </div>
            </div>
            <!-- Actions -->
            <div class="flex items-center gap-1 shrink-0">
              <button v-if="$checkPermission('schedulers.update') && s.status !== 'paused' && !s.is_running" @click="pause(s._id)" class="p-2 rounded-lg text-gray-400 hover:text-amber-500 hover:bg-amber-50 transition-colors" title="Pause">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
              </button>
              <button v-if="$checkPermission('schedulers.update') && s.status === 'paused'" @click="resume(s._id)" class="p-2 rounded-lg text-gray-400 hover:text-green-500 hover:bg-green-50 transition-colors" title="Resume">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
              </button>
              <button v-if="$checkPermission('schedulers.update')" @click="openForm(s)" class="p-2 rounded-lg text-gray-400 hover:text-[var(--color-primary)] hover:bg-[var(--color-primary-light)] transition-colors" title="Edit">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              </button>
              <button v-if="$checkPermission('schedulers.delete')" @click="confirmDelete(s._id)" class="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors" title="Delete">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
              </button>
            </div>
          </div>
        </div>
        <div v-else class="px-6 py-16 text-center">
          <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          </div>
          <p class="text-base font-medium text-gray-900">No schedules yet</p>
          <p class="text-sm text-gray-500 mt-1 mb-5">Create your first schedule to automate this channel</p>
          <button @click="openForm(null)" class="btn-primary">
            <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add Schedule
          </button>
        </div>
    </div>

    </div><!-- end card -->

    <!-- Schedule form modal -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showForm" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div class="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] flex flex-col animate-fade-in">
            <div class="flex items-center justify-between px-6 pt-6 shrink-0">
              <h3 class="text-lg font-semibold text-gray-900">{{ form._id ? 'Edit Schedule' : 'New Schedule' }} <span v-if="form._id" class="text-sm font-normal text-gray-400">({{ form._id }})</span></h3>
              <button @click="showForm = false" class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <form @submit.prevent="save" class="flex-1 flex flex-col overflow-hidden">
              <div class="flex-1 overflow-y-auto p-6 pt-5 space-y-5">
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1.5">Title</label>
                <input v-model="form.title" type="text" placeholder="Schedule title" />
              </div>

              <div class="grid grid-cols-2 gap-3">
                <label :class="['flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all', form.recurrency === 'recurrent' ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)]' : 'border-gray-200 hover:border-gray-300']">
                  <input type="radio" name="recurrency" value="recurrent" v-model="form.recurrency" class="mt-0.5 accent-[var(--color-primary)]" />
                  <div>
                    <p class="text-sm font-semibold" :class="form.recurrency === 'recurrent' ? 'text-[var(--color-primary)]' : 'text-gray-700'">Recurrent</p>
                    <p class="text-xs text-gray-400 mt-0.5">Will run recurrently</p>
                  </div>
                </label>
                <label :class="['flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all', form.recurrency === 'one-time' ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)]' : 'border-gray-200 hover:border-gray-300']">
                  <input type="radio" name="recurrency" value="one-time" v-model="form.recurrency" class="mt-0.5 accent-[var(--color-primary)]" />
                  <div>
                    <p class="text-sm font-semibold" :class="form.recurrency === 'one-time' ? 'text-[var(--color-primary)]' : 'text-gray-700'">One Time</p>
                    <p class="text-xs text-gray-400 mt-0.5">Will run just one time</p>
                  </div>
                </label>
              </div>

              <div class="flex items-center justify-between py-1">
                <div>
                  <p class="text-sm font-medium" :class="recordingMode !== 'schedule' ? 'text-gray-400' : 'text-gray-700'">Enable Recording</p>
                  <p class="text-sm text-gray-400">Create a recording clip during this schedule window</p>
                </div>
                <ToggleSwitch v-model="form.enable_recording" :disabled="recordingMode !== 'schedule'" />
              </div>

              <div v-if="recordingMode !== 'schedule'" class="flex items-start gap-2.5 px-3.5 py-2.5 rounded-lg bg-amber-50 border border-amber-100">
                <svg class="w-4 h-4 text-amber-500 shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                <p class="text-sm text-amber-700">Recording mode must be set to <span class="font-medium">"Schedule"</span> on the livestream to enable this option.</p>
              </div>

              <div v-if="form.enable_recording && recordingMode === 'schedule'" class="flex items-start gap-2.5 px-3.5 py-2.5 rounded-lg bg-blue-50 border border-blue-100">
                <svg class="w-4 h-4 text-blue-500 shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                <p class="text-sm text-blue-700">This schedule will only manage recording — it will not start or stop the channel.</p>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1.5">Timezone</label>
                <select v-model="form.timezone">
                  <option v-for="tz in timezoneOptions" :key="tz.value" :value="tz.value">{{ tz.label }}</option>
                </select>
              </div>

              <div class="bg-gray-50 rounded-xl p-4 space-y-4">
                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="block text-sm font-medium text-gray-600 mb-1.5">From Date</label>
                    <VueDatePicker v-model="form.from_date" text-input hide-input-icon :is-24="false" :enable-time-picker="true" :clearable="true" placeholder="From Date" teleport />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-600 mb-1.5">To Date</label>
                    <VueDatePicker v-model="form.to_date" text-input hide-input-icon :is-24="false" :enable-time-picker="true" :clearable="true" placeholder="To Date" teleport />
                  </div>
                </div>
                <div v-if="form.recurrency === 'recurrent'" class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="block text-sm font-medium text-gray-600 mb-1.5">Start Time</label>
                    <VueDatePicker v-model="form.start_time" text-input hide-input-icon :clearable="true" time-picker placeholder="Start Time" teleport />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-600 mb-1.5">End Time</label>
                    <VueDatePicker v-model="form.end_time" text-input hide-input-icon :clearable="true" time-picker placeholder="End Time" teleport />
                  </div>
                </div>
              </div>

              <div v-if="form.recurrency === 'recurrent'">
                <label class="block text-sm font-medium text-gray-600 mb-2">Frequency</label>
                <div class="grid grid-cols-4 gap-2">
                  <label v-for="day in weekDays" :key="day"
                    :class="['flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all text-xs font-medium',
                      form.frequency.includes(day) ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)] text-[var(--color-primary)]' : 'border-gray-200 text-gray-500 hover:border-gray-300']"
                  >
                    <input type="checkbox" :value="day" v-model="form.frequency" class="hidden" />
                    {{ day.slice(0, 3) }}
                  </label>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1.5">Tags</label>
                <TagSelect v-model="form.tags" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1.5">Category</label>
                <CategorySelect v-model="form.categories" />
              </div>

              <!-- Custom Attributes -->
              <CustomAttributes
                :resource-id="form._id || null"
                module="schedules"
                @updated="form.attributes = $event"
              />

              </div>
              <div class="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 shrink-0">
                <button type="button" @click="showForm = false" class="btn-secondary">Cancel</button>
                <button type="submit" :disabled="saving" class="btn-primary">{{ saving ? 'Saving...' : 'Save' }}</button>
              </div>
            </form>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Hidden lookups -->
    <TagSelect ref="tagLookup" class="hidden" preload />
    <CategorySelect ref="categoryLookup" class="hidden" preload />

    <!-- Confirm action -->
    <ConfirmModal
      :show="showConfirm"
      :title="confirmTitle"
      :message="confirmMessage"
      :confirm-text="confirmText"
      :danger="confirmText === 'Delete'"
      :loading="confirmLoading"
      @confirm="doAction"
      @cancel="showConfirm = false"
    />
  </div>
</template>

<script>
import api from '../../../services/api.js'
import { notifySuccess, notifyError } from '../../../services/notifications.js'
import ConfirmModal from '../../../components/ConfirmModal.vue'
import ToggleSwitch from '../../../components/ToggleSwitch.vue'
import TagSelect from '../../../components/TagSelect.vue'
import CategorySelect from '../../../components/CategorySelect.vue'
import CustomAttributes from '../../../components/CustomAttributes.vue'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'

export default {
  name: 'LiveScheduler',
  components: { ConfirmModal, ToggleSwitch, TagSelect, CategorySelect, CustomAttributes, VueDatePicker },
  props: {
    livestreamId: { type: String, required: true },
    recordingMode: { type: String, default: 'channel' },
    defaultTags: { type: Array, default: () => [] },
    defaultCategories: { type: Array, default: () => [] }
  },

  data() {
    return {
      activeTab: 'today',
      // Today
      todaySchedules: [],
      loadingToday: true,
      // All
      schedules: [],
      loadingAll: true,
      // Form
      showForm: false,
      saving: false,
      form: this.defaultForm(),
      weekDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      timezoneOptions: [
        { value: 'America/New_York', label: 'Eastern Time (ET)' },
        { value: 'America/Chicago', label: 'Central Time (CT)' },
        { value: 'America/Denver', label: 'Mountain Time (MT)' },
        { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
        { value: 'America/Puerto_Rico', label: 'Atlantic Time (AT)' },
        { value: 'UTC', label: 'UTC' },
        { value: 'Europe/London', label: 'London (GMT/BST)' },
        { value: 'Europe/Paris', label: 'Paris (CET)' },
        { value: 'Asia/Tokyo', label: 'Tokyo (JST)' }
      ],
      // Time display toggle (key = schedule _id, value = true for original)
      showOriginalTime: {},
      // Confirm
      showConfirm: false,
      confirmTitle: '',
      confirmMessage: '',
      confirmText: '',
      confirmLoading: false,
      confirmFn: null
    }
  },

  computed: {
    localTzShort() {
      const tz = this.detectTimezone()
      return this.getTimezoneLabel(tz)
    }
  },

  watch: {
    activeTab: {
      immediate: true,
      handler(tab) {
        if (tab === 'today') this.loadToday()
        else if (tab === 'manage') this.loadAll()
      }
    }
  },

  methods: {
    defaultForm() {
      return {
        _id: null,
        title: '',
        recurrency: 'recurrent',
        timezone: this.detectTimezone(),
        from_date: null,
        to_date: null,
        start_time: null,
        end_time: null,
        frequency: [],
        orientation: 'horizontal',
        enable_recording: false,
        status: null,
        tags: [...this.defaultTags],
        categories: [...this.defaultCategories]
      }
    },

    toggleTimeDisplay(id) {
      this.showOriginalTime = { ...this.showOriginalTime, [id]: !this.showOriginalTime[id] }
    },

    toLocalTime(s, which) {
      const timeObj = which === 'start' ? s.start_time : s.end_time
      if (!timeObj || typeof timeObj !== 'object' || timeObj.hours === undefined) {
        return which === 'start' ? this.formatStartTime(s) : this.formatEndTime(s)
      }
      const tz = s.timezone || 'America/New_York'
      try {
        // Create a date in the schedule's timezone, then format in local
        const now = new Date()
        const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T${String(timeObj.hours).padStart(2, '0')}:${String(timeObj.minutes || 0).padStart(2, '0')}:00`
        const fmt = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: tz })
        const localFmt = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
        // Parse the date as if it's in the source timezone
        // Use the formatter to get the UTC equivalent, then convert
        const sourceDate = new Date(dateStr)
        const sourceStr = fmt.format(sourceDate)
        const localStr = localFmt.format(sourceDate)
        // If source tz matches local tz, they'll be the same
        if (sourceStr === localStr && tz === this.detectTimezone()) return localStr
        // Otherwise compute the offset
        const sourceOffset = this.getTzOffsetMs(tz, sourceDate)
        const localOffset = sourceDate.getTimezoneOffset() * -60000
        const diff = localOffset - sourceOffset
        const localDate = new Date(sourceDate.getTime() + diff)
        return localFmt.format(localDate)
      } catch {
        return which === 'start' ? this.formatStartTime(s) : this.formatEndTime(s)
      }
    },

    getTzOffsetMs(tz, date) {
      const utcStr = date.toLocaleString('en-US', { timeZone: 'UTC' })
      const tzStr = date.toLocaleString('en-US', { timeZone: tz })
      return new Date(tzStr).getTime() - new Date(utcStr).getTime()
    },

    getTimezoneLabel(tz) {
      const opt = this.timezoneOptions.find(o => o.value === tz)
      return opt ? opt.label.replace(/^.*\(/, '').replace(/\)$/, '') : tz
    },

    detectTimezone() {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
        if (this.timezoneOptions.some(o => o.value === tz)) return tz
      } catch { /* ignore */ }
      return 'America/New_York'
    },

    formatDisplayTime(s, which, original) {
      if (s.recurrency === 'one-time') {
        const dt = which === 'start' ? s.start_datetime : (s.end_datetime || s.to_date)
        if (!dt) return '--'
        const date = new Date(dt)
        const fmtOpts = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }
        if (original) {
          try {
            return new Intl.DateTimeFormat('en-US', { ...fmtOpts, timeZone: s.timezone || 'America/New_York' }).format(date)
          } catch { return this.$filters.formatDatetime(dt) }
        }
        return new Intl.DateTimeFormat('en-US', fmtOpts).format(date)
      }
      // Recurrent: use start_time / end_time objects
      if (original) {
        return which === 'start' ? this.formatStartTime(s) : this.formatEndTime(s)
      }
      return this.toLocalTime(s, which)
    },

    formatStartTime(s) {
      if (s.recurrency === 'recurrent' && s.start_time) {
        if (typeof s.start_time === 'object' && s.start_time.hours !== undefined) {
          const h = s.start_time.hours
          const m = String(s.start_time.minutes || 0).padStart(2, '0')
          const period = h >= 12 ? 'PM' : 'AM'
          const h12 = h % 12 || 12
          return `${h12}:${m} ${period}`
        }
        return this.$filters.formatTime(s.start_time)
      }
      if (s.start_datetime) return this.$filters.formatDatetime(s.start_datetime)
      return '--'
    },

    formatEndTime(s) {
      if (s.recurrency === 'recurrent' && s.end_time) {
        if (typeof s.end_time === 'object' && s.end_time.hours !== undefined) {
          const h = s.end_time.hours
          const m = String(s.end_time.minutes || 0).padStart(2, '0')
          const period = h >= 12 ? 'PM' : 'AM'
          const h12 = h % 12 || 12
          return `${h12}:${m} ${period}`
        }
      }
      // Fallback to duration if end_time not set
      if (s.duration) return this.$filters.formatDuration(s.duration)
      return '--'
    },

    async loadToday() {
      this.loadingToday = true
      try {
        const res = await api.get('/schedulers/by-date', { params: { live_id: this.livestreamId }, silent: true })
        this.todaySchedules = res.data?.items || []
      } catch { /* silent */ } finally { this.loadingToday = false }
    },

    async loadAll() {
      this.loadingAll = true
      try {
        const res = await api.get('/schedulers/', { params: { live_id: this.livestreamId, target: 'coming-up' }, silent: true })
        this.schedules = res.data?.items || []
      } catch { /* silent */ } finally { this.loadingAll = false }
    },

    openForm(item) {
      if (item) {
        const form = { ...item }
        // Convert UTC dates from API to the schedule's timezone for the date picker
        const tz = form.timezone || 'America/New_York'
        if (form.from_date) form.from_date = this.utcToTzDate(form.from_date, tz)
        if (form.to_date) form.to_date = this.utcToTzDate(form.to_date, tz)
        this.form = form
      } else {
        this.form = this.defaultForm()
      }
      this.showForm = true
    },

    utcToTzDate(utcStr, tz) {
      try {
        const utcDate = new Date(utcStr)
        // Format the UTC date in the target timezone to get the "local" components
        const parts = new Intl.DateTimeFormat('en-US', {
          timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
        }).formatToParts(utcDate)
        const get = (type) => parts.find(p => p.type === type)?.value || '0'
        // Build a local Date from the timezone-converted components
        return new Date(+get('year'), +get('month') - 1, +get('day'), +get('hour') === 24 ? 0 : +get('hour'), +get('minute'), +get('second'))
      } catch {
        return new Date(utcStr)
      }
    },

    validate() {
      const v = this.form
      if (!v.title?.trim()) { notifyError('Title is required'); return false }
      if (!v.from_date) { notifyError('From Date is required'); return false }
      if (v.recurrency === 'recurrent') {
        if (!v.start_time) { notifyError('Start Time is required'); return false }
        if (!v.end_time) { notifyError('End Time is required'); return false }
        if (!v.frequency?.length) { notifyError('Select at least one week day'); return false }
        // End time must be after start time
        const startMin = this.timeToMinutes(v.start_time)
        const endMin = this.timeToMinutes(v.end_time)
        if (startMin != null && endMin != null && endMin <= startMin) {
          notifyError('End Time must be after Start Time'); return false
        }
      } else {
        if (!v.to_date) { notifyError('End Date is required'); return false }
      }
      // To date must be after from date
      if (v.from_date && v.to_date) {
        const from = v.from_date instanceof Date ? v.from_date : new Date(v.from_date)
        const to = v.to_date instanceof Date ? v.to_date : new Date(v.to_date)
        if (to <= from) { notifyError('To Date must be after From Date'); return false }
      }

      // Recording schedule requires recording_mode = 'schedule' on the live
      if (v.enable_recording && this.recordingMode !== 'schedule') {
        notifyError('Recording schedules require "Schedule" recording mode on the livestream. Update the Recording Mode in Transcoder Control first.')
        return false
      }

      // Overlap check (2 min gap required)
      const overlap = this.checkOverlap(v)
      if (overlap) {
        notifyError(`Overlaps with "${overlap.title}" — schedules must have at least 2 minute gap`)
        return false
      }

      return true
    },

    // Convert time object {hours, minutes} to minutes since midnight
    timeToMinutes(t) {
      if (!t || typeof t !== 'object') return null
      return (t.hours || 0) * 60 + (t.minutes || 0)
    },

    // Check if two date ranges overlap
    dateRangesOverlap(from1, to1, from2, to2) {
      const a = from1 ? new Date(from1).getTime() : -Infinity
      const b = to1 ? new Date(to1).getTime() : Infinity
      const c = from2 ? new Date(from2).getTime() : -Infinity
      const d = to2 ? new Date(to2).getTime() : Infinity
      return a <= d && b >= c
    },

    // Check if two frequency arrays share any day
    daysOverlap(freq1, freq2) {
      if (!freq1?.length || !freq2?.length) return true // if no days specified, assume all days
      return freq1.some(d => freq2.includes(d))
    },

    checkOverlap(form) {
      const GAP = 2 // 2 minutes
      const editingId = form._id

      // Deduplicate schedules from both tabs
      const seen = new Set()
      const others = [...this.todaySchedules, ...this.schedules].filter(s => {
        if (s._id === editingId || seen.has(s._id)) return false
        seen.add(s._id)
        return true
      })

      const newFromDate = form.from_date
      const newToDate = form.to_date

      for (const s of others) {
        // 1. Check date range overlap (from_date/to_date)
        const existFrom = s.from_date
        const existTo = s.to_date
        if (!this.dateRangesOverlap(newFromDate, newToDate, existFrom, existTo)) continue

        if (form.recurrency === 'recurrent' && s.recurrency === 'recurrent') {
          // 2. Check shared week days
          if (!this.daysOverlap(form.frequency, s.frequency)) continue

          // 3. Check time overlap with gap
          let newStart = this.timeToMinutes(form.start_time)
          let newEnd = this.timeToMinutes(form.end_time)
          let existStart = this.timeToMinutes(s.start_time)
          let existEnd = this.timeToMinutes(s.end_time)
          if (newStart == null || newEnd == null || existStart == null || existEnd == null) continue
          if (newEnd <= newStart) newEnd += 24 * 60
          if (existEnd <= existStart) existEnd += 24 * 60

          if (newStart < existEnd + GAP && newEnd > existStart - GAP) return s

        } else if (form.recurrency !== 'recurrent' && s.recurrency !== 'recurrent') {
          // Both one-time: compare full datetime ranges
          const newStartMs = new Date(newFromDate).getTime()
          const newEndMs = new Date(newToDate).getTime()
          const existStartMs = new Date(s.start_datetime || s.from_date).getTime()
          const existEndMs = new Date(s.end_datetime || s.to_date).getTime()
          const gapMs = GAP * 60000

          if (newStartMs < existEndMs + gapMs && newEndMs > existStartMs - gapMs) return s

        } else {
          // Cross-type: recurrent vs one-time
          const recurrent = form.recurrency === 'recurrent' ? form : s
          const oneTime = form.recurrency === 'recurrent' ? s : form

          // Check if one-time's day of week matches recurrent's frequency
          const otFrom = new Date(oneTime.start_datetime || oneTime.from_date)
          const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
          const otDay = dayNames[otFrom.getDay()]
          if (recurrent.frequency?.length && !recurrent.frequency.includes(otDay)) continue

          // Compare time-of-day
          let recStart = this.timeToMinutes(recurrent.start_time)
          let recEnd = this.timeToMinutes(recurrent.end_time)
          if (recStart == null || recEnd == null) continue
          if (recEnd <= recStart) recEnd += 24 * 60

          const otStart = otFrom.getHours() * 60 + otFrom.getMinutes()
          const otEndDate = new Date(oneTime.end_datetime || oneTime.to_date)
          let otEnd = otEndDate.getHours() * 60 + otEndDate.getMinutes()
          if (otEnd <= otStart) otEnd += 24 * 60

          if (otStart < recEnd + GAP && otEnd > recStart - GAP) return s
        }
      }

      return null
    },

    toLocalDateString(d) {
      if (!d) return null
      const date = d instanceof Date ? d : new Date(d)
      if (isNaN(date)) return null
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const h = String(date.getHours()).padStart(2, '0')
      const min = String(date.getMinutes()).padStart(2, '0')
      const s = String(date.getSeconds()).padStart(2, '0')
      return `${y}-${m}-${day}T${h}:${min}:${s}`
    },

    async save() {
      if (!this.validate()) return
      this.saving = true
      try {
        const values = { ...this.form, live_id: this.livestreamId, attributes: this.form.attributes || [] }
        // Format dates as local datetime strings (no timezone suffix)
        if (values.from_date) values.from_date = this.toLocalDateString(values.from_date)
        if (values.to_date) values.to_date = this.toLocalDateString(values.to_date)
        const id = values._id
        delete values._id
        if (id) {
          await api.put(`/schedulers/${id}`, { schedule: values })
        } else {
          await api.post('/schedulers', { schedule: values })
        }
        notifySuccess('Schedule saved')
        this.showForm = false
        this.loadToday()
        this.loadAll()
      } catch (err) {
        notifyError(err?.message || 'Failed to save schedule')
      } finally { this.saving = false }
    },

    pause(id) {
      this.confirmTitle = 'Pause Schedule'
      this.confirmMessage = 'Are you sure you want to pause this schedule?'
      this.confirmText = 'Pause'
      this.confirmFn = async () => { await api.put(`/schedulers/${id}/pause`); this.loadAll() }
      this.showConfirm = true
    },

    resume(id) {
      this.confirmTitle = 'Resume Schedule'
      this.confirmMessage = 'Are you sure you want to resume this schedule?'
      this.confirmText = 'Resume'
      this.confirmFn = async () => { await api.put(`/schedulers/${id}/resume`); this.loadAll() }
      this.showConfirm = true
    },

    confirmDelete(id) {
      this.confirmTitle = 'Delete Schedule'
      this.confirmMessage = 'Are you sure you want to delete this schedule?'
      this.confirmText = 'Delete'
      this.confirmFn = async () => { await api.delete(`/schedulers/${id}`); this.loadAll() }
      this.showConfirm = true
    },

    async doAction() {
      if (!this.confirmFn) return
      this.confirmLoading = true
      try {
        await this.confirmFn()
        notifySuccess(`Schedule ${this.confirmText.toLowerCase()}d`)
        this.showConfirm = false
      } catch (err) {
        notifyError(err?.message || 'Action failed')
      } finally { this.confirmLoading = false }
    }
  }
}
</script>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: opacity 0.2s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
</style>
